import { DestroyRef, inject, Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { isObservable, Observable, of, Subject } from 'rxjs';
import { filter, map, mergeMap, shareReplay, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseNavigationCallback, BaseStep } from '@lx/quote-tool/lite/shared/types';

/**
 * Base navigation service for managing step-based navigation in quote tools.
 *
 * This service is decoupled from any specific store implementation and uses an
 * event-driven architecture. It exposes a `currentStep$` observable that emits
 * the current step whenever navigation occurs.
 *
 * Applications should compose this service (not extend) and subscribe to `currentStep$`
 * to react to navigation changes without creating tight coupling.
 *
 * @template TRouteId - The type of route identifier used in the application (e.g., enum, string literal union)
 *
 * @example
 * ```typescript
 * // In application-specific NavigationService
 * export class NavigationService {
 *   private readonly baseNavigationService = inject(BaseNavigationService<RouteIds>);
 *
 *   constructor() {
 *     this.baseNavigationService.currentStep$
 *       .pipe(takeUntilDestroyed())
 *       .subscribe(step => this.store.updateCurrentStep(step));
 *   }
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class BaseNavigationService<TRouteId = string> {
  /**
   * Observable that emits the current step whenever navigation occurs.
   * Consumers can subscribe to this to react to navigation changes.
   */
  readonly currentStep$: Observable<BaseStep<TRouteId>>;

  private readonly currentStepSubject = new Subject<BaseStep<TRouteId>>();
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.currentStep$ = this.currentStepSubject.pipe(shareReplay({ bufferSize: 1, refCount: true }));
    this.prepareSubscriptionToNavigationEvents();
  }

  /**
   * Navigates to the next step based on the step's nextStep callback
   * @param step - The current step containing the nextStep callback
   */
  navigateToNextStep(step: BaseStep<TRouteId>): void {
    const { nextStep } = step;
    this.handleStepNavigation(nextStep);
  }

  /**
   * Navigates to a specific step by route ID
   * @param step - The route ID to navigate to
   * @param callback - Optional callback to execute after navigation completes
   */
  navigateToStep(step: TRouteId, callback?: () => void): void {
    this.router
      .navigate([step], {
        replaceUrl: true,
      })
      .then(() => {
        if (callback) {
          callback();
        }
      });
  }

  /**
   * Navigates to the first step in the flow
   * @param callback - Optional callback to execute after navigation completes
   *
   * Note: This method assumes 'course' is the first step. For apps with different
   * first steps, override this method or use navigateToStep directly.
   */
  navigateToFirstStep(callback?: () => void): void {
    this.navigateToStep('course' as TRouteId, callback);
  }

  /**
   * Handles navigation based on a step callback
   * @param stepCallback - The callback that returns the next route ID
   * @private
   */
  private handleStepNavigation(stepCallback: BaseNavigationCallback<TRouteId> | undefined): void {
    if (!stepCallback) {
      console.warn('Navigation to step requested, but no transition was declared.');
      return;
    }

    const callbackReturnValue = stepCallback();
    (isObservable(callbackReturnValue) ? callbackReturnValue : of(callbackReturnValue))
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((route: TRouteId) => {
        this.navigateToStep(route);
      });
  }

  /**
   * Subscribes to router navigation events and emits the current step.
   *
   * When a NavigationEnd event occurs, it extracts the step definition from the
   * route data and emits it through the currentStep$ observable.
   *
   * Consumers can subscribe to currentStep$ to react to these navigation changes
   * without creating a tight coupling to this service.
   *
   * @private
   */
  private prepareSubscriptionToNavigationEvents(): void {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        mergeMap((route) => route.data),
        map((data) => data['step'] as BaseStep<TRouteId>),
        shareReplay(1),
        takeUntilDestroyed(),
      )
      .subscribe((step: BaseStep<TRouteId>) => {
        // Emit the step to all subscribers
        this.currentStepSubject.next(step);
      });
  }
}
